import { apiFetch } from "./client";

export type GbpHours = { day: string; open: string; close: string; status: string };
export type GbpLocation = {
  id: string;
  title: string;
  address: string;
  addressRegionCode: string;
  addressAdministrativeArea: string;
  addressLocality: string;
  addressPostalCode: string;
  addressLines: string[];
  phone: string;
  websiteUri: string;
  primaryCategory: string;
  openStatus: string;
  regularHours: GbpHours[];
  isEnabled: boolean;
  isDefault: boolean;
  lastSyncedAt?: string | null;
};
export type GbpReview = {
  name: string;
  rating: number;
  reviewerDisplayName: string;
  createTime: string;
  comment: string;
  hasReply: boolean;
  replyText?: string;
};
export type GbpSettings = {
  configured: boolean;
  connection: { id: string; googleEmail: string; status: string; lastSyncedAt?: string | null } | null;
  locations: GbpLocation[];
  lastSelectedLocationId?: string | null;
  currentLocation?: GbpLocation;
};
export type GbpDashboard = {
  location: GbpLocation;
  rating: number;
  reviewCount: number;
  metrics30d: { searches: number; calls: number; websiteClicks: number; directions: number };
  metricSeries30d?: Array<{ date: string; searches: number; calls: number; websiteClicks: number; directions: number }>;
  latestReviews: GbpReview[];
  lastSyncedAt?: string | null;
  reviewError?: string | null;
  performanceError?: string | null;
};
export type GbpBusinessInfo = {
  location: GbpLocation;
  editableFields: string[];
  readOnlyFields: string[];
  lastSyncedAt?: string | null;
  validatedOnly?: boolean;
};
export type GbpPerformance = {
  location: GbpLocation;
  period: number;
  totals: { searches: number; calls: number; websiteClicks: number; directions: number };
  daily: Array<{ date: string; searches: number; calls: number; websiteClicks: number; directions: number }>;
  lastSyncedAt?: string | null;
  aiAnalysis: { enabled: boolean; text: string; generatedAt?: string | null };
};
export type GbpAiInsight = {
  summary: string;
  insights: Array<{
    severity: "positive" | "warning" | "critical" | "neutral";
    title: string;
    description: string;
  }>;
  actions: Array<{ action_id: string; priority: "high" | "medium" | "low" }>;
};
export type GbpAiInsightResponse = {
  text: string;
  status: string;
  usage?: Record<string, unknown>;
  insight?: GbpAiInsight;
};

function params(locationId?: string | null, extra?: Record<string, string | number | boolean | undefined>) {
  const sp = new URLSearchParams();
  if (locationId) sp.set("locationId", locationId);
  Object.entries(extra || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== false) sp.set(k, String(v === true ? 1 : v));
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

export const googleBusinessApi = {
  getSettings: () => apiFetch<GbpSettings>("/google-business/settings"),
  updateLocationSettings: (body: { enabledLocationIds: string[]; defaultLocationId: string | null }) =>
    apiFetch<GbpSettings>("/google-business/settings/locations", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  getAuthUrl: () => apiFetch<{ url: string }>("/google-business/auth/url"),
  deleteConnection: () => apiFetch<{ status: string }>("/google-business/connection", { method: "DELETE" }),
  getLocations: () => apiFetch<GbpSettings>("/google-business/locations"),
  setCurrentLocation: (locationId: string) =>
    apiFetch<{ currentLocation: GbpLocation }>("/google-business/locations/current", {
      method: "POST",
      body: JSON.stringify({ locationId }),
    }),
  getDashboard: (locationId?: string | null, refresh = false) =>
    apiFetch<GbpDashboard>(`/google-business/dashboard${params(locationId, { refresh })}`),
  getReviews: (locationId?: string | null, refresh = false) =>
    apiFetch<{ location: GbpLocation; reviews: GbpReview[]; lastSyncedAt?: string | null }>(
      `/google-business/reviews${params(locationId, { refresh })}`,
    ),
  replyReview: (locationId: string, reviewName: string, text: string) =>
    apiFetch<{ location: GbpLocation; reviews: GbpReview[] }>("/google-business/reviews/reply", {
      method: "PUT",
      body: JSON.stringify({ locationId, reviewName, text }),
    }),
  getBusinessInfo: (locationId?: string | null, refresh = false) =>
    apiFetch<GbpBusinessInfo>(`/google-business/business-info${params(locationId, { refresh })}`),
  patchBusinessInfo: (body: {
    locationId: string;
    title?: string;
    address?: string;
    addressRegionCode?: string;
    addressLocality?: string;
    addressAdministrativeArea?: string;
    addressPostalCode?: string;
    phone?: string;
    websiteUri?: string;
    regularHours?: GbpHours[];
    primaryCategory?: string;
    openStatus?: string;
    validateOnly?: boolean;
  }) =>
    apiFetch<GbpBusinessInfo>("/google-business/business-info", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  getPerformance: (locationId?: string | null, period = 30, refresh = false) =>
    apiFetch<GbpPerformance>(`/google-business/performance${params(locationId, { period, refresh })}`),
  refreshAnalysis: (locationId: string, period: number) =>
    apiFetch<GbpPerformance>("/google-business/performance/analysis", {
      method: "POST",
      body: JSON.stringify({ locationId, period }),
    }),
  generateAiInsight: (body: { locationId?: string | null; period?: number }) =>
    apiFetch<GbpAiInsightResponse>("/google-business/ai-insight", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
